import React, { forwardRef, useEffect, useRef, useState } from "react";
import styled from "styled-components/macro";
import { getMapIcon, MapIcons } from "./MapIcons";
import { MapContext } from "./MapContext";
import { MapInitializer } from "./MapInitializer";
import { getMapLoader } from "./utils";

export interface IMap {
  zoom?: number;
  center: google.maps.MapOptions["center"];
  panTo?: google.maps.MapOptions["center"];
  bounds?: google.maps.LatLngBounds;
  markers?: IMarker[];
  children?: React.ReactNode;
  onDragStart?: (location?: google.maps.LatLngLiteral) => void;
  onDragEnd?: (location: google.maps.LatLngLiteral) => void;
  onBoundsChanged?: (
    bounds: google.maps.LatLngBoundsLiteral,
    size: { width: number; height: number },
    center: google.maps.LatLngLiteral
  ) => void;
  onZoomChanged?: (zoomLevel: number) => void;
  onElementSizeChanged?: (size: { width: number; height: number }) => void;
  searchRadius?: number;
  searchRadiusCenter?: google.maps.MapOptions["center"];
}

export interface IMarker {
  id: string;
  position: google.maps.MarkerOptions["position"];
  icon?: google.maps.Icon | MapIcons;
  title?: string;
  data?: any;
  selected?: boolean;
  active?: boolean;
}

export const MapRoot = styled.div`
  height: 100%;
  width: 100%;
`;

export interface IMapRef {
  map: google.maps.Map;
  panTo: (coordinates: google.maps.LatLng | google.maps.LatLngLiteral) => void;
}
export const GeoMap = forwardRef<IMapRef, IMap>(
  (
    {
      zoom,
      center,
      panTo,
      bounds,
      markers,
      children,
      onDragStart,
      onDragEnd,
      onBoundsChanged,
      onZoomChanged,
      searchRadius,
      searchRadiusCenter,
      onElementSizeChanged
    },
    ref
  ) => {
    const mapsLibraryRef = useRef<google.maps.MapsLibrary | null>(null);
    const [map, setMap] = useState<google.maps.Map | null>(null);
    const mapElementRef = useRef<HTMLDivElement | null>(null);


    useEffect(() => {
      const loader = getMapLoader();

      loader.load().then(async () => {
        if (!mapElementRef.current) {
          return;
        }
        const mapsLibrary = (await google.maps.importLibrary(
          "maps"
        )) as google.maps.MapsLibrary;
        mapsLibraryRef.current = mapsLibrary as google.maps.MapsLibrary;

        const map = new mapsLibrary.Map(mapElementRef.current, {
          center: { lat: -34.397, lng: 150.644 },
          zoom: 8,
          minZoom: 6,
          maxZoom: 16,
          zoomControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          mapTypeControl: false,
          styles: [
            {
              featureType: "poi",
              elementType: "labels",
              stylers: [{ visibility: "off" }]
            }
          ]
        });

        setMap(map);
      });
    }, []);

    useEffect(() => {
      if (map && center) {
        map.setCenter(center);
      }
    }, [map, center]);

    useEffect(() => {
      if (map && typeof zoom === "number") {
        map.setZoom(zoom);
      }
    }, [map, zoom]);

    useEffect(() => {
      const addedMarkers: google.maps.Marker[] = [];

      if (map && markers?.length) {
        for (const marker of markers) {
          let icon;
          if (typeof marker.icon === "string" && marker.icon in MapIcons) {
            icon = getMapIcon(marker.icon);
          } else {
            icon = marker.icon;
          }
          addedMarkers.push(
            new google.maps.Marker({
              map,
              position: marker.position,
              title: marker.title,
              icon
            })
          );
        }
      }

      return () => {
        for (const marker of addedMarkers) {
          marker.setMap(null);
        }
      };
    }, [map, markers]);

    useEffect(() => {
      if (map && panTo) {
        map?.panTo(panTo);
      }
    }, [map, panTo]);

    useEffect(() => {
      if (map && ref) {
        if (typeof ref !== "function") {
          ref.current = {
            map: map,
            panTo: (
              coordinates: google.maps.LatLng | google.maps.LatLngLiteral
            ) => {
              map.panTo(coordinates);
            }
          };
        }
      }
    }, [map, ref]);

    useEffect(() => {
      if (map && onDragStart) {
        const listener = map.addListener("dragstart", () => {
          onDragStart(map.getCenter()?.toJSON());
        });
        return () => {
          if (listener) {
            listener.remove();
          }
        };
      }
    }, [map, onDragStart]);

    useEffect(() => {
      if (map && onDragEnd) {
        let dragFinished = false;
        const dragEndListener = map.addListener("dragend", () => {
          dragFinished = true;
        });
        const idleListener = map.addListener("idle", () => {
          if (dragFinished) {
            dragFinished = false;
            const loc = map.getCenter()?.toJSON();
            if (!loc) {
              return;
            }
            onDragEnd(loc);
          }
        });

        return () => {
          idleListener.remove();
          dragEndListener.remove();
        };
      }
    }, [map, onDragEnd]);

    useEffect(() => {
      if (map && onZoomChanged) {
        const eventListener = map.addListener("zoom_changed", () => {
          const zoom = map.getZoom();
          if (!zoom) {
            return;
          }
          onZoomChanged(zoom);
        });

        return () => {
          eventListener.remove();
        };
      }
    }, [map, onZoomChanged]);

    useEffect(() => {
      if (map && onBoundsChanged) {
        const eventListener = map.addListener("bounds_changed", () => {
          const bounds = map.getBounds();
          if (!bounds) {
            return;
          }

          const spherical = google.maps.geometry.spherical;
          const width = spherical.computeDistanceBetween(
            {
              lat: bounds.getNorthEast().lat(),
              lng: bounds.getNorthEast().lng()
            },
            {
              lat: bounds.getNorthEast().lat(),
              lng: bounds.getSouthWest().lng()
            }
          );

          const height = spherical.computeDistanceBetween(
            {
              lat: bounds.getNorthEast().lat(),
              lng: bounds.getNorthEast().lng()
            },
            {
              lat: bounds.getSouthWest().lat(),
              lng: bounds.getNorthEast().lng()
            }
          );

          onBoundsChanged(
            bounds.toJSON(),
            {
              width,
              height
            },
            map.getCenter()?.toJSON()!
          );
        });

        return () => {
          eventListener.remove();
        };
      }
    }, [map, onBoundsChanged]);

    useEffect(() => {
      if (map && bounds) {
        map.fitBounds(bounds, 0);
      }
    }, [map, bounds]);

    useEffect(() => {
      if (map && onElementSizeChanged && mapElementRef.current) {
        const resizeObserver = new ResizeObserver((entries) => {
          if (entries[0]) {
            const size = entries[0].contentRect.toJSON();
            onElementSizeChanged({
              width: size.width,
              height: size.height
            });
          }
        });

        resizeObserver.observe(mapElementRef.current);

        return () => {
          resizeObserver.disconnect();
        };
      }
    }, [map, onElementSizeChanged]);

    useEffect(() => {
      if (map && searchRadius && searchRadiusCenter) {
        const marker = new google.maps.Marker({
          map: map,
          position: searchRadiusCenter,
          icon: {
            path: "M 0, 16 a 16,16 0 1,1 32,0 a 16,16 0 1,1 -32,0",
            fillColor: "black",
            fillOpacity: 1,
            strokeWeight: 0,
            rotation: 0,
            scale: 0.01, // marker is very small to be invisible on the map
            anchor: new google.maps.Point(16, 16),
            labelOrigin: new google.maps.Point(16, 16)
          }
        });
        const circle = new google.maps.Circle({
          strokeColor: "#000000",
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillOpacity: 0,
          map: map,
          center: searchRadiusCenter,
          radius: searchRadius // in meters
        });
        circle.bindTo("center", marker, "position");

        return () => {
          marker.setMap(null);
          circle.unbindAll();
          circle.setMap(null);
        };
      }
    }, [map, searchRadiusCenter, searchRadius]);

    return (
      <>
        <MapRoot ref={mapElementRef} />
        <MapInitializer>
          <MapContext.Provider value={map}>{children}</MapContext.Provider>
        </MapInitializer>
      </>
    );
  }
);
